import { ArrowLeft, Eye } from "lucide-react";
import { T, hexA } from "@/uniq/theme";
import { stripCustomerNameQuestions } from "@shared-domain/survey/logic";
import { phaseNoteText } from "@shared-domain/survey/phaseLabel";
import { QTYPE } from "@/uniq/questionMeta";
import { OptionMark } from "@/uniq/OptionMark";
import type { Question, Survey } from "@shared-domain/survey/contract";

/** 設問タイプごとの中身 (選択肢・スケール範囲など) を閲覧専用で描画。 */
function QuestionBody({ q }: { q: Question }) {
  if (q.type === "radio") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
        {q.options.map((o) => {
          const tone = o.tone === "ok" ? T.ok : o.tone === "warn" ? T.warn : null;
          return (
            <div key={o.v} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155" }}>
              <OptionMark kind="radio" />
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                {tone && <span style={{ width: 7, height: 7, borderRadius: 9999, background: tone, flex: "none" }} />}
                {o.label}
              </span>
              {o.withText && <span style={{ fontSize: 10.5, color: T.faint, background: "#F1F5F9", borderRadius: 9999, padding: "1px 7px", flex: "none" }}>内容を入力</span>}
            </div>
          );
        })}
      </div>
    );
  }
  if (q.type === "checkbox") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
        {q.options.map((o) => (
          <div key={o.v} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155" }}>
            <OptionMark kind="checkbox" />
            <span style={{ minWidth: 0 }}>{o.label}</span>
          </div>
        ))}
      </div>
    );
  }
  if (q.type === "scale") {
    return (
      <div style={{ marginTop: 8, fontSize: 13, color: "#334155" }}>
        {q.scaleMin} 〜 {q.scaleMax}
        {(q.minLabel || q.maxLabel) && (
          <span style={{ color: T.faint, marginLeft: 8, fontSize: 12 }}>
            {q.minLabel && `${q.scaleMin}=${q.minLabel}`}{q.minLabel && q.maxLabel && " ／ "}{q.maxLabel && `${q.scaleMax}=${q.maxLabel}`}
          </span>
        )}
      </div>
    );
  }
  if (q.type === "text" && q.placeholder)
    return <div style={{ marginTop: 8, fontSize: 12, color: T.faint }}>例: {q.placeholder}</div>;
  return null;
}

/** アンケート定義の閲覧専用ビュー (PRODUCT_DEV-574)。回答者がアンケート名タップで内容確認する用途。 */
export function SurveyDef({ survey, onBack }: { survey: Survey; onBack: () => void }) {
  const qs = stripCustomerNameQuestions(survey.questions);
  return (
    <>
      <div style={{ background: "#fff", padding: "10px 12px", display: "flex", alignItems: "center", gap: 6, borderBottom: `1px solid ${T.line}` }}>
        <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: 9999, background: "transparent", border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowLeft size={20} color={T.ink} /></button>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: T.ink }}>アンケート内容</div>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: T.sub, background: "#F1F5F9", borderRadius: 9999, padding: "4px 10px", display: "inline-flex", alignItems: "center", gap: 4 }}><Eye size={11} color={T.sub} />閲覧のみ</span>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "14px 16px 24px", background: T.bg }}>
        <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,.05)" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>{survey.title}</div>
          {survey.description && <div style={{ fontSize: 12.5, color: T.sub, marginTop: 6, whiteSpace: "pre-wrap" }}>{survey.description}</div>}
          {(survey.issuer || survey.deadline) && (
            <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 12, color: T.sub }}>
              {survey.issuer && <span>発行元 <b style={{ color: "#475569" }}>{survey.issuer}</b></span>}
              {survey.deadline && <span>期日 <b style={{ color: "#475569" }}>{survey.deadline}</b></span>}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: "13px 16px", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>対象顧客名</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.danger, background: hexA(T.danger, 0.08), borderRadius: 9999, padding: "1px 7px" }}>必須</span>
            </div>
            <div style={{ fontSize: 12, color: T.faint, marginTop: 4 }}>回答対象の顧客を選択・入力します</div>
          </div>
          {qs.map((q, i) => {
            const note = phaseNoteText(q.phase, survey.questions);
            return (
              <div key={q.questionId} style={{ background: "#fff", borderRadius: 14, padding: "13px 16px", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.faint, flex: "none" }}>Q{i + 1}</span>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, minWidth: 0 }}>{q.label}</span>
                  {q.required && <span style={{ fontSize: 10, fontWeight: 700, color: T.danger, background: hexA(T.danger, 0.08), borderRadius: 9999, padding: "1px 7px", flex: "none" }}>必須</span>}
                  <span style={{ fontSize: 10, fontWeight: 600, color: T.sub, background: "#F1F5F9", borderRadius: 9999, padding: "1px 7px", flex: "none", marginLeft: "auto" }}>{QTYPE[q.type].label}</span>
                </div>
                {note && <div style={{ fontSize: 11, color: T.warn, marginTop: 5 }}>{note}</div>}
                {q.hint && <div style={{ fontSize: 11.5, color: T.faint, marginTop: 5 }}>{q.hint}</div>}
                <QuestionBody q={q} />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
